import React from "react";
import styled, { keyframes } from "styled-components";
import { Link as GatsbyLink } from "gatsby";

import { DialogContent, DialogOverlay } from "@reach/dialog";
import {
  AtlasButton,
  Box,
  Text,
  Link,
  Logo,
  themeGet,
  VisuallyHidden,
  useTheme,
} from "@fuegokit/react";
import { ColorModeToggle } from "../ColorModeToggle";
import { Search } from "../Search";
import useSiteMetadata from "../../hooks/use-site-metadata";
import fuegokitNavItems from "../../fuegokit-nav.yml";
import SidePanel from "../SidePanel";
import { HeaderNavDropdown } from "../HeaderNavDropdown";
import { AfMenuIcon } from "@fuegokit/fuegoicons-react";
export const HEADER_HEIGHT = 64;

const Header = () => {
  const siteMetadata = useSiteMetadata();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { colorMode } = useTheme();

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          borderBottom: "1px solid",
          borderBottomColor: "border.subtle",
          backgroundColor: "elevation.surface.raised.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: `${HEADER_HEIGHT}px`,
            px: [3, null, null, 4],
            alignItems: "center",
            justifyContent: "space-between",
            bg: "elevation.surface.default.[default]",
          }}
          as="nav"
          aria-labelledby="mainmenulabel"
        >
          <DesktopNavigation>
            <StyledLink
              href="https://fuegokit.design"
              sx={{
                color: "text.accent.blue.default",
                mr: 2,
                lineHeight: "condensedUltra",
              }}
            >
              <Box sx={{ color: "text.default" }}>
                <VisuallyHidden id="mainmenulabel">Main menu</VisuallyHidden>

                <Logo
                  variant="solid"
                  size={16}
                  color={colorMode === "night" ? "#fff" : "#000"}
                  aria-hidden="true"
                />
              </Box>
            </StyledLink>
            <StyledLink
              href="https://fuegokit.design"
              sx={{
                color: "text.default",
                fontSize: 4,
              }}
            >
              Fuegokit
            </StyledLink>

            {siteMetadata.siteName && siteMetadata.siteName !== "Fuegokit" ? (
              <>
                <Text
                  sx={{
                    display: "inline-block",
                    color: "text.default",
                    mx: 2,
                  }}
                >
                  /
                </Text>
                <StyledLink
                  as={GatsbyLink}
                  to="/"
                  sx={{
                    width: "max-content",
                    color: "text.default",
                    fontWeight: "semibold",
                  }}
                >
                  {siteMetadata.siteName}
                </StyledLink>
              </>
            ) : null}
          </DesktopNavigation>

          <MobileNavigation>
            <>
              {siteMetadata.siteName && siteMetadata.siteName !== "Fuegokit" ? (
                <AtlasButton
                  spacing="default"
                  appearance={"subtle"}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <VisuallyHidden>Open menu</VisuallyHidden>
                  <AfMenuIcon size={"small"} />
                </AtlasButton>
              ) : null}
            </>
            {siteMetadata.siteName && siteMetadata.siteName === "Fuegokit" ? (
              <StyledLink
                href="https://fuegokit.design"
                sx={{
                  color: "text.accent.blue.default",
                  mr: 2,
                  lineHeight: "condensedUltra",
                }}
              >
                {siteMetadata.siteName}
              </StyledLink>
            ) : null}
            {siteMetadata.siteName && siteMetadata.siteName !== "Fuegokit" ? (
              <>
                <StyledLink
                  as={GatsbyLink}
                  to="/"
                  sx={{ width: "max-content", color: "text.default" }}
                >
                  {siteMetadata.siteName}
                </StyledLink>
              </>
            ) : null}
          </MobileNavigation>

          <DesktopActions>
            {siteMetadata && siteMetadata.isSearchEnabled ? <Search /> : null}

            <StyledLink
              href="https://fuegokit.design/news"
              sx={{
                display: "inline-block",
                color: "inherit",
                ml: 4,
                fontWeight: "normal",
                fontSize: 3,
              }}
            >
              What&apos;s New
            </StyledLink>
            <StyledLink
              href={`https://appfireworkspace.slack.com/archives/C03CTJZ4BUH`}
              sx={{
                display: "flex",
                gap: 1,
                color: "inherit",
                ml: 4,
                fontWeight: "normal",
                fontSize: 3,
                alignItems: "center",
                color: "text.default",
              }}
            >
              Log in
            </StyledLink>
          </DesktopActions>
          <HeaderActions>
            <HeaderNavDropdown items={fuegokitNavItems} />
            <ColorModeToggle />
          </HeaderActions>
        </Box>
      </Box>

      <MobileNavWrapper
        isOpen={isExpanded}
        onDismiss={() => setIsExpanded(false)}
      >
        <MobileMenuShelf>
          <SidePanel />
        </MobileMenuShelf>
      </MobileNavWrapper>
    </>
  );
};

const StyledLink = styled(Link)`
  text-decoration: none;
  opacity: 0.85;
  &:hover {
    opacity: 1;
    text-decoration: none;
  }
`;

const DesktopActions = styled(Box)`
  display: none;
  @media (min-width: ${themeGet("breakpoints.1")}) {
    display: flex;
    margin-left: 24px;
    white-space: nowrap;
    margin-left: auto;
    align-items: center;
  }
`;

const HeaderActions = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${themeGet("space.1")};
`;

const DesktopNavigation = styled(Box)`
  display: none;
  @media (min-width: ${themeGet("breakpoints.1")}) {
    display: flex;
    align-items: center;
  }
`;
const MobileNavigation = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${themeGet("space.1")};
  @media (min-width: ${themeGet("breakpoints.1")}) {
    display: none;
  }
`;

const MobileNavWrapper = styled(DialogOverlay)`
  position: fixed;
  top: 68px;
  left: -16px;
  right: 0;
  bottom: 0;
  background: ${themeGet("colors.blanket.default")};
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0%)
  }
`;

const MobileMenuShelf = styled(DialogContent)`
  position: absolute;
  top: -16px;
  left: 0;
  bottom: 0;
  padding-top: ${themeGet("space.3")};
  padding-right: ${themeGet("space.4")};
  padding-bottom: ${themeGet("space.3")};
  padding-left: ${themeGet("space.2")};
  animation: ${slideIn} 500ms;
`;

export default Header;
